import type { PauseMode, Task } from '@dyspatch/core';
import { Link } from '@tanstack/react-router';
import { Check, Pause, PencilLine, Play, Save, X } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/input';
import { AskBox, useAsk } from '@/features/actions/AskBox';
import type { Verb } from '@/features/actions/verbs';
import { useHubAction } from '@/hooks/use-hub-action';
import { api } from '@/lib/api';
import { briefMovesTo, taskMovesTo } from '@/lib/optimistic';
import { cn } from '@/lib/utils';

/**
 * Pause, resume, the gate for added steps, and "change this task"
 * (docs/JOB-LIFECYCLE.md 2.5 and 2.6). Their own file rather than more rows in
 * `GateActions.tsx`, which is another agent's this wave; the row shape and the
 * touch sizing are the same, copied rather than imported so the two files stay
 * independent until the merge.
 */
function Row({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('flex flex-wrap items-center gap-1.5 px-gutter py-2', className)}>
      {children}
    </div>
  );
}

const TOUCH = 'max-sm:h-9 max-sm:px-3';

/** The one chip every paused row wears, so a paused job reads as paused from the list. */
export function PausedChip({ className }: { className?: string }) {
  return (
    <Badge variant="warning" className={className} title="Paused by a person. Resume to continue.">
      <Pause className="size-2.5" aria-hidden />
      Paused
    </Badge>
  );
}

function asMode(choice: string | undefined): PauseMode {
  return choice === 'finish' ? 'finish' : 'checkpoint';
}

/**
 * Pause or Resume, on one step. Pause asks in place with the two choices;
 * Resume does not ask, because it undoes a pause and nothing is lost by
 * pressing it. No optimistic edit: a pause changes a flag rather than a status,
 * and the row's copy comes from the refetch a moment later.
 */
export function PauseActions({ task, className }: { task: Task; className?: string }) {
  const ask = useAsk();
  const pause = useHubAction(
    (value: string, choice?: string) => api.pauseTask(task.id, asMode(choice), value || undefined),
    { onDone: () => ask.close(), success: 'Paused. The work is kept.' },
  );
  const resume = useHubAction(() => api.resumeTask(task.id), {
    success: task.branch ? `Resumed; the next attempt continues from ${task.branch}.` : 'Resumed.',
  });

  return (
    <div className={className}>
      <Row>
        {task.pausedAt ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => resume.run()}
            disabled={resume.busy}
            className={TOUCH}
          >
            <Play aria-hidden />
            {resume.busy ? 'Resuming…' : 'Resume'}
          </Button>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={() => ask.open('pause', task.id)}
            className={TOUCH}
          >
            <Pause aria-hidden />
            Pause
          </Button>
        )}
      </Row>
      {ask.ask ? (
        <AskBox
          ask={ask.ask}
          busy={pause.busy}
          error={pause.error}
          onCancel={ask.close}
          onConfirm={(value, choice) => pause.run(value, choice)}
        />
      ) : null}
    </div>
  );
}

/**
 * Pause or Resume, for every unfinished step of a job at once.
 *
 * Each button is drawn only when a press of it would do something. A job stays
 * `approved` until its last step ends, so "every step is already held" and
 * "every step is already over" are both states this row can be in, and in both
 * of them the hub answers a pause with a 409. Drawing the button there made the
 * pause look broken: the row did not change, because there was nothing left in
 * it to change.
 */
export function PauseBriefActions({
  briefId,
  pausableCount,
  pausedCount,
  className,
}: {
  briefId: string;
  /** How many steps a press would hold: unfinished, and not already held. */
  pausableCount: number;
  /** How many steps carry the flag; Resume is offered while any do. */
  pausedCount: number;
  className?: string;
}) {
  const ask = useAsk();
  const pause = useHubAction(
    (value: string, choice?: string) => api.pauseBrief(briefId, asMode(choice), value || undefined),
    { onDone: () => ask.close(), success: 'Job paused. The work is kept.' },
  );
  const resume = useHubAction(() => api.resumeBrief(briefId), {
    success: 'Job resumed; paused tasks are eligible again.',
  });

  return (
    <div className={className}>
      <Row>
        {pausableCount > 0 ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => ask.open('pauseBrief', briefId)}
            className={TOUCH}
          >
            <Pause aria-hidden />
            {pausedCount > 0
              ? `Pause ${pausableCount} more task${pausableCount === 1 ? '' : 's'}`
              : 'Pause job'}
          </Button>
        ) : null}
        {pausedCount > 0 ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => resume.run()}
            disabled={resume.busy}
            className={TOUCH}
          >
            <Play aria-hidden />
            {resume.busy
              ? 'Resuming…'
              : `Resume ${pausedCount} paused task${pausedCount === 1 ? '' : 's'}`}
          </Button>
        ) : null}
      </Row>
      {ask.ask ? (
        <AskBox
          ask={ask.ask}
          busy={pause.busy}
          error={pause.error}
          onCancel={ask.close}
          onConfirm={(value, choice) => pause.run(value, choice)}
        />
      ) : null}
    </div>
  );
}

/**
 * The gate, again, for the steps an amendment added. The same route as Go; the
 * hub queues only the drafts and leaves the rest of the job as it was.
 */
export function AddedTasksActions({
  briefId,
  count,
  className,
}: {
  briefId: string;
  count: number;
  className?: string;
}) {
  const ask = useAsk();
  const approve = useHubAction(() => api.approveBrief(briefId), {
    onDone: () => ask.close(),
    success: `${count} added task${count === 1 ? '' : 's'} queued.`,
    // The brief is already approved; what this restates is the row, which is
    // already right. Nothing to move optimistically.
    optimistic: () => briefMovesTo(briefId, 'approved'),
  });

  return (
    <div className={className}>
      <Row>
        <Button
          size="sm"
          variant="gate"
          data-gate-verb="primary"
          onClick={() => ask.open('approveAdded', briefId)}
          className={TOUCH}
        >
          <Check aria-hidden />
          Approve added tasks
        </Button>
      </Row>
      {ask.ask ? (
        <AskBox
          ask={ask.ask}
          busy={approve.busy}
          error={approve.error}
          onCancel={ask.close}
          onConfirm={() => approve.run()}
        />
      ) : null}
    </div>
  );
}

/**
 * "Change this task": one verb that does the right thing for the step's state
 * and says which (docs/JOB-LIFECYCLE.md 2.6).
 *
 *   - draft or queued: the spec is edited in place and the next offer carries it.
 *   - running: a running agent takes a note, not a rewrite; the copy says so and
 *     points at the notes thread (another wave builds it).
 *   - review: the change goes back as feedback, which is the existing send-back.
 *   - anything else: the spec is history.
 *
 * The editor is a textarea rather than the one-line ask box because a spec is a
 * page, not a sentence.
 */
export function ChangeTaskActions({ task, className }: { task: Task; className?: string }) {
  const [open, setOpen] = useState(false);
  const [spec, setSpec] = useState(task.spec);
  const ask = useAsk();

  // Another task in the same pane brings its own spec, not the last one's.
  useEffect(() => {
    setSpec(task.spec);
    setOpen(false);
  }, [task.spec]);

  const save = useHubAction((text: string) => api.setTaskSpec(task.id, text), {
    onDone: () => setOpen(false),
    success: 'Spec changed. The next offer carries the new text.',
    inlineError: true,
  });
  const sendBack = useHubAction(
    (verb: Verb, value: string) => {
      if (verb === 'changes') return api.requeueTask(task.id, { feedback: value });
      throw new Error(`no change action for ${verb}`);
    },
    { onDone: () => ask.close(), optimistic: () => taskMovesTo(task.id, 'queued') },
  );

  const editable = task.status === 'draft' || task.status === 'queued';
  const running =
    task.status === 'claimed' || task.status === 'running' || task.status === 'reporting';
  const inReview = task.status === 'review';
  if (!editable && !running && !inReview && task.status !== 'offered') return null;

  return (
    <div className={className}>
      <Row>
        <Button
          size="sm"
          variant="outline"
          className={TOUCH}
          onClick={() => {
            if (editable) setOpen((current) => !current);
            else if (inReview) ask.open('changes', task.id);
          }}
          disabled={running || task.status === 'offered'}
          title={
            running
              ? 'A running task takes a note, not a new spec.'
              : task.status === 'offered'
                ? 'Just offered to a device. Pause it first, then change it.'
                : undefined
          }
        >
          <PencilLine aria-hidden />
          Change this task
        </Button>
        {running ? (
          <span className="text-fg-3 text-2xs">
            It is running: leave it a{' '}
            <Link
              to="/tasks/$taskId"
              params={{ taskId: task.id }}
              hash="notes"
              className="underline"
            >
              note
            </Link>{' '}
            and the agent reads it within a heartbeat.
          </span>
        ) : inReview ? (
          <span className="text-fg-3 text-2xs">
            It is in review: the change goes back to the agent as feedback.
          </span>
        ) : null}
      </Row>
      {editable && open ? (
        <form
          className="bg-sunken border-t px-gutter py-2"
          onKeyDown={(event) => {
            if (event.key !== 'Escape') return;
            event.stopPropagation();
            setOpen(false);
          }}
          onSubmit={(event) => {
            event.preventDefault();
            if (spec.trim().length < 10) return;
            save.run(spec.trim());
          }}
        >
          <p className="text-fg-2 mb-1.5 text-xs">
            {task.status === 'queued'
              ? 'Rewrite the spec. Nothing is running it yet; the device that picks it up reads this text.'
              : 'Rewrite the spec. It is approved with the rest of the added tasks.'}
          </p>
          <Textarea
            value={spec}
            onChange={(event) => setSpec(event.target.value)}
            rows={8}
            minLength={10}
            maxLength={20_000}
            aria-label="The task's spec"
            className="font-mono text-xs"
          />
          <div className="mt-1.5 flex items-center gap-1.5">
            <Button
              type="submit"
              size="sm"
              disabled={save.busy || spec.trim().length < 10 || spec.trim() === task.spec}
            >
              <Save aria-hidden />
              {save.busy ? 'Saving…' : 'Save spec'}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setOpen(false)}>
              <X aria-hidden />
              Cancel
            </Button>
          </div>
          {save.error ? (
            <p className="text-bad pt-1.5 text-xs" role="alert">
              {save.error}
            </p>
          ) : null}
        </form>
      ) : null}
      {ask.ask ? (
        <AskBox
          ask={ask.ask}
          busy={sendBack.busy}
          error={sendBack.error}
          onCancel={ask.close}
          onConfirm={(value) => {
            if (ask.ask) sendBack.run(ask.ask.verb, value);
          }}
        />
      ) : null}
    </div>
  );
}
